import React, { useState, useEffect, useRef } from 'react';
import Sidebar from './Sidebar';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash, FaClock, FaSearch,
         FaNewspaper, FaCloudUploadAlt, FaTimes } from 'react-icons/fa';
import { safeFetch } from '../utils/apiUtils';
import './AdminBlog.css';

const API_BASE = 'https://aayushrealproperties.in/nimaibackend/public/api';
const getToken = () => localStorage.getItem('admin_token');
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const CATEGORIES = ['General', 'Web Development', 'Mobile Development', 'UI/UX Design', 'SEO & Performance', 'Business Growth', 'Case Study'];

const emptyPost = {
    title:          '',
    slug:           '',
    excerpt:        '',
    content:        '',
    category:       'General',
    author:         'Nimai Solutions',
    is_published:   false,
    read_time:      5,
    featured_image: null,
};

const slugify = (str) =>
    str.toLowerCase().trim()
       .replace(/[^\w\s-]/g, '')
       .replace(/[\s_-]+/g, '-')
       .replace(/^-+|-+$/g, '');

const AdminBlog = () => {
    const [posts, setPosts]           = useState([]);
    const [isLoading, setIsLoading]   = useState(true);
    const [search, setSearch]         = useState('');
    const [filterCat, setFilterCat]   = useState('All');

    // Modal
    const [showModal, setShowModal]   = useState(false);
    const [editingId, setEditingId]   = useState(null);
    const [form, setForm]             = useState(emptyPost);
    const [imgPreview, setImgPreview] = useState(null);
    const [isSaving, setIsSaving]     = useState(false);

    // Delete confirm
    const [deleteId, setDeleteId]     = useState(null);

    const fileRef = useRef();

    /* ─── FETCH ─── */
    const fetchPosts = async () => {
        setIsLoading(true);
        try {
            const { ok, data: json } = await safeFetch(
                `${API_BASE}/admin/blog-posts`,
                { headers: authHeaders() }
            );
            if (ok) setPosts(Array.isArray(json.data) ? json.data : []);
            else     toast.error('Failed to load posts');
        } catch {
            toast.error('Failed to load posts');
            setPosts([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { fetchPosts(); }, []);

    /* ─── FILTERED LIST ─── */
    const filtered = posts.filter(p => {
        const matchSearch = p.title.toLowerCase().includes(search.toLowerCase()) ||
                            (p.category || '').toLowerCase().includes(search.toLowerCase());
        const matchCat    = filterCat === 'All' || p.category === filterCat;
        return matchSearch && matchCat;
    });

    /* ─── OPEN / CLOSE MODAL ─── */
    const openNew = () => {
        setForm(emptyPost);
        setImgPreview(null);
        setEditingId(null);
        setShowModal(true);
    };

    const openEdit = (post) => {
        setForm({
            title:          post.title || '',
            slug:           post.slug  || '',
            excerpt:        post.excerpt  || '',
            content:        post.content  || '',
            category:       post.category || 'General',
            author:         post.author   || 'Nimai Solutions',
            is_published:   !!post.is_published,
            read_time:      post.read_time || 5,
            featured_image: null,
        });
        setImgPreview(post.featured_image || null);
        setEditingId(post.id);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
        setForm(emptyPost);
        setImgPreview(null);
    };

    /* ─── FORM HANDLERS ─── */
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(f => ({
            ...f,
            [name]: type === 'checkbox' ? checked : value,
            ...(name === 'title' && !editingId ? { slug: slugify(value) } : {}),
        }));
    };

    const handleFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setForm(f => ({ ...f, featured_image: file }));
        setImgPreview(URL.createObjectURL(file));
    };

    /* ─── SAVE ─── */
    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) { toast.error('Title is required'); return; }

        setIsSaving(true);
        const fd = new FormData();
        Object.entries(form).forEach(([key, val]) => {
            if (key === 'featured_image') {
                if (val && typeof val !== 'string') fd.append('featured_image', val);
            } else if (key === 'is_published') {
                fd.append('is_published', val ? '1' : '0');
            } else {
                fd.append(key, val ?? '');
            }
        });
        if (editingId) fd.append('_method', 'PUT');

        try {
            const url = editingId ? `${API_BASE}/blog-posts/${editingId}` : `${API_BASE}/blog-posts`;
            const { ok, data: json } = await safeFetch(url, {
                method:  'POST',
                headers: authHeaders(),
                body: fd,
            });

            if (ok) {
                toast.success(editingId ? 'Post updated!' : 'Post created!');
                closeModal();
                fetchPosts();
            } else {
                const errMsg = json?.errors
                    ? Object.values(json.errors).flat().join(', ')
                    : json?.message || 'Error saving post';
                toast.error(errMsg);
            }
        } catch {
            toast.error('Network error');
        } finally {
            setIsSaving(false);
        }
    };

    /* ─── DELETE ─── */
    const confirmDelete = async () => {
        if (!deleteId) return;
        try {
            const { ok } = await safeFetch(`${API_BASE}/blog-posts/${deleteId}`, {
                method:  'DELETE',
                headers: authHeaders(),
            });
            if (ok) {
                toast.success('Post deleted');
                fetchPosts();
            } else {
                toast.error('Failed to delete');
            }
        } catch {
            toast.error('Network error');
        } finally {
            setDeleteId(null);
        }
    };

    /* ─── QUICK TOGGLE PUBLISH ─── */
    const togglePublish = async (post) => {
        const fd = new FormData();
        fd.append('_method', 'PUT');
        fd.append('is_published', post.is_published ? '0' : '1');
        try {
            const { ok } = await safeFetch(`${API_BASE}/blog-posts/${post.id}`, {
                method:  'POST',
                headers: authHeaders(),
                body: fd,
            });
            if (ok) {
                toast.success(post.is_published ? 'Post unpublished' : 'Post published!');
                fetchPosts();
            }
        } catch {
            toast.error('Network error');
        }
    };

    /* ─── RENDER ─── */
    return (
        <div className="admin-layout">
            <Sidebar />

            <main className="admin-content">
                {/* ── Page Header ── */}
                <header className="content-header">
                    <div>
                        <h1>Blog <span>Posts</span></h1>
                        <p>{posts.length} total &nbsp;·&nbsp; {posts.filter(p => p.is_published).length} published</p>
                    </div>
                    <button className="add-btn" onClick={openNew}>
                        <FaPlus /> New Post
                    </button>
                </header>

                {/* ── Filters ── */}
                <div className="blog-admin-filters">
                    <div className="blog-admin-search">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search posts..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="blog-cat-tabs">
                        {['All', ...CATEGORIES].map(c => (
                            <button
                                key={c}
                                className={filterCat === c ? 'active' : ''}
                                onClick={() => setFilterCat(c)}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Content ── */}
                {isLoading ? (
                    <div className="admin-loading-container">
                        <div className="admin-loader" />
                        <p>Loading posts...</p>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="blog-empty-state">
                        <FaNewspaper className="empty-icon" />
                        <h3>No posts found</h3>
                        <p>{search ? `No results for "${search}"` : 'Click "New Post" to create your first blog post.'}</p>
                        {!search && (
                            <button className="add-btn" style={{ marginTop: 20 }} onClick={openNew}>
                                <FaPlus /> Create First Post
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="blog-table-wrap">
                        <table className="blog-table">
                            <thead>
                                <tr>
                                    <th>Post</th>
                                    <th>Category</th>
                                    <th>Status</th>
                                    <th>Read Time</th>
                                    <th>Views</th>
                                    <th>Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((post, i) => (
                                    <motion.tr
                                        key={post.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.04 }}
                                    >
                                        <td>
                                            <div className="blog-title-cell">
                                                {post.featured_image
                                                    ? <img src={post.featured_image} alt="" className="blog-row-thumb" />
                                                    : <div className="blog-row-thumb-placeholder"><FaNewspaper /></div>
                                                }
                                                <div>
                                                    <span className="blog-row-title">{post.title}</span>
                                                    <span className="blog-row-slug">/{post.slug}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td><span className="badge-cat">{post.category}</span></td>
                                        <td>
                                            <span className={`badge-status ${post.is_published ? 'published' : 'draft'}`}>
                                                {post.is_published ? '● Published' : '○ Draft'}
                                            </span>
                                        </td>
                                        <td className="cell-muted"><FaClock style={{ marginRight: 5 }} />{post.read_time} min</td>
                                        <td className="cell-muted">{post.views}</td>
                                        <td className="cell-muted">{new Date(post.created_at).toLocaleDateString('en-IN')}</td>
                                        <td>
                                            <div className="blog-row-actions">
                                                <button
                                                    className={`row-btn ${post.is_published ? 'btn-unpublish' : 'btn-publish'}`}
                                                    title={post.is_published ? 'Unpublish' : 'Publish'}
                                                    onClick={() => togglePublish(post)}
                                                >
                                                    {post.is_published ? <FaEyeSlash /> : <FaEye />}
                                                </button>
                                                <button className="row-btn btn-edit" onClick={() => openEdit(post)}>
                                                    <FaEdit />
                                                </button>
                                                <button className="row-btn btn-delete" onClick={() => setDeleteId(post.id)}>
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>

            {/* ─── POST MODAL ─── */}
            <AnimatePresence>
                {showModal && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={(e) => e.target === e.currentTarget && closeModal()}
                    >
                        <motion.div
                            className="blog-modal"
                            initial={{ scale: 0.93, opacity: 0, y: 20 }}
                            animate={{ scale: 1,    opacity: 1, y: 0 }}
                            exit={{ scale: 0.93, opacity: 0, y: 20 }}
                            transition={{ duration: 0.22 }}
                        >
                            <div className="modal-header">
                                <h2>{editingId ? 'Edit Post' : 'New Blog Post'}</h2>
                                <button className="close-modal" onClick={closeModal}><FaTimes /></button>
                            </div>

                            <form onSubmit={handleSave} className="blog-form">
                                {/* Title */}
                                <div className="form-group full-width">
                                    <label>Title *</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={form.title}
                                        onChange={handleChange}
                                        placeholder="Enter post title"
                                        required
                                    />
                                </div>

                                {/* Slug + Category */}
                                <div className="form-group">
                                    <label>Slug (URL)</label>
                                    <input
                                        type="text"
                                        name="slug"
                                        value={form.slug}
                                        onChange={handleChange}
                                        placeholder="auto-generated-from-title"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Category</label>
                                    <select name="category" value={form.category} onChange={handleChange}>
                                        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>

                                {/* Author + Read Time */}
                                <div className="form-group">
                                    <label>Author</label>
                                    <input
                                        type="text"
                                        name="author"
                                        value={form.author}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Read Time (minutes)</label>
                                    <input
                                        type="number"
                                        name="read_time"
                                        value={form.read_time}
                                        onChange={handleChange}
                                        min={1}
                                        max={60}
                                    />
                                </div>

                                {/* Excerpt */}
                                <div className="form-group full-width">
                                    <label>
                                        Excerpt
                                        <span className="char-hint">{(form.excerpt || '').length}/500</span>
                                    </label>
                                    <textarea
                                        name="excerpt"
                                        value={form.excerpt}
                                        onChange={handleChange}
                                        placeholder="Brief summary shown in blog listing cards..."
                                        rows={3}
                                        maxLength={500}
                                    />
                                </div>

                                {/* Content */}
                                <div className="form-group full-width">
                                    <label>Content <span className="label-hint">(HTML supported)</span></label>
                                    <textarea
                                        name="content"
                                        value={form.content}
                                        onChange={handleChange}
                                        placeholder="<h2>Introduction</h2>&#10;<p>Your content here...</p>"
                                        rows={12}
                                        className="code-editor"
                                    />
                                </div>

                                {/* Image + Publish */}
                                <div className="form-group">
                                    <label>Featured Image</label>
                                    <div
                                        className="img-drop-zone"
                                        onClick={() => fileRef.current.click()}
                                    >
                                        {imgPreview
                                            ? <img src={imgPreview} alt="preview" />
                                            : <>
                                                <FaCloudUploadAlt className="upload-icon" />
                                                <span>Click to upload</span>
                                              </>
                                        }
                                    </div>
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={handleFile}
                                    />
                                </div>

                                <div className="form-group publish-group">
                                    <label>Publish Status</label>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            name="is_published"
                                            checked={form.is_published}
                                            onChange={handleChange}
                                        />
                                        <span className="switch-track" />
                                        <span className="switch-label">
                                            {form.is_published ? '✓ Published' : '○ Draft'}
                                        </span>
                                    </label>
                                </div>

                                {/* Footer */}
                                <div className="form-footer full-width">
                                    <button type="button" className="btn-cancel" onClick={closeModal}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="save-btn" disabled={isSaving}>
                                        {isSaving ? 'Saving...' : (editingId ? 'Update Post' : 'Create Post')}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ─── DELETE CONFIRM ─── */}
            <AnimatePresence>
                {deleteId && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="confirm-modal"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1,   opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                        >
                            <div className="confirm-icon">🗑️</div>
                            <h3>Delete this post?</h3>
                            <p>This action cannot be undone.</p>
                            <div className="confirm-actions">
                                <button className="btn-cancel" onClick={() => setDeleteId(null)}>Cancel</button>
                                <button className="btn-confirm-delete" onClick={confirmDelete}>Yes, Delete</button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminBlog;
